import { useEffect, useState } from "react";
import { BrandLogo } from "../brand/BrandLogo";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowUpRight } from "lucide-react";
import { NAV, OM_OSS, INVESTOR_LINKS } from "../../lib/nav";
import { getCached } from "../../lib/api";
import { CompanyLogo } from "../brand/CompanyLogo";
import { useInView } from "../motion/Reveal";
import { isEn, langHref, t } from "../../lib/i18n";

const Col = ({ title, children }) => (
  <div>
    <h3 className="t-label text-white/70 mb-6">{title}</h3>
    <ul className="space-y-3 text-[15px]">{children}</ul>
  </div>
);

const A = ({ href, external, children, testid }) =>
  external ? (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className="inline-flex items-center gap-1 hover:text-white transition-colors duration-200">{children}<ArrowUpRight size={13} className="opacity-60" /></a>
  ) : (
    <Link to={href} data-testid={testid} className="hover:text-white transition-colors duration-200">{children}</Link>
  );

export const Footer = ({ settings = {} }) => {
  const a = settings.address || {};
  const [companies, setCompanies] = useState([]);
  // The footer is mounted on every page; only fetch the company list and its logos once it is near the viewport.
  const [nearRef, near] = useInView({ rootMargin: "600px 0px", threshold: 0 });
  useEffect(() => { if (near) getCached("/companies", 10 * 60_000).then(setCompanies).catch(() => {}); }, [near]);

  return (
    <footer ref={nearRef} data-testid="footer-container" className="relative z-10 bg-navy text-white/70 border-t border-white/10">
      <div className="wrap pt-24 pb-16 md:pt-32 md:pb-20">
        <div className="flex items-end justify-between gap-8">
          <BrandLogo variant="white" className="footer-mark" alt="EQVA" data-testid="footer-brand-logo" />
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="footer-to-top" className="group mb-2 hidden md:inline-flex items-center gap-2 t-small text-white/70 hover:text-white transition-colors">
            {t("Til toppen")} <span className="grid h-10 w-10 place-items-center rounded-[3px] border border-white/25 transition-colors group-hover:bg-white group-hover:text-navy"><ArrowUp size={16} /></span>
          </button>
        </div>
        <div className="mt-10 grid-12 gap-y-4 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p data-testid="footer-statement" className="text-[clamp(1.5rem,2.4vw,2.25rem)] font-medium leading-[1.1] tracking-[-0.025em] text-white">{settings.footer_statement || t("Sterke selskaper bygges over tid.")}</p>
            <p className="mt-4 max-w-[46ch] t-body text-white/70">{settings.footer_text || t("Vi investerer i industriselskaper og menneskene som bygger dem – og utvikler dem videre sammen.")}</p>
          </div>
          <p className="col-span-12 lg:col-span-4 lg:text-right t-small text-white/60">Sunde i Sunnhordland</p>
        </div>

        <div className="mt-16 md:mt-24 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <h3 className="t-label text-white/70 mb-6">{t("Kontakt")}</h3>
            <address className="not-italic text-[15px] leading-relaxed" data-testid="footer-address">
              <span className="text-white">{a.name || "EQVA ASA"}</span><br />
              {a.street || "Sunde Industriområde 3"}<br />
              {a.postal || "5450 Sunde i Sunnhordland"}
            </address>
            <p className="mt-4 text-[15px] leading-relaxed">{t("Postadresse")}<br />{(a.postal_address || "Dronning Eufemias gate 16, 0191 Oslo").split(/,\s*/).map((l, i) => <span key={i} className="block">{l}</span>)}</p>
            <a href={`mailto:${a.email || "office@eqva.no"}`} data-testid="footer-email" className="mt-4 inline-block text-[15px] text-white link-underline">{a.email || "office@eqva.no"}</a>
          </div>
          <Col title={t("Utforsk")}>
            {NAV.map((n) => (
              <li key={n.href}><A href={n.href} testid={`footer-link-${n.id}`}>{n.label}</A></li>
            ))}
          </Col>
          <Col title={t("Om EQVA")}>
            {OM_OSS.map((n) => (
              <li key={n.href}><A href={n.href} testid={`footer-omoss-${n.href.split("/").pop()}`}>{n.label}</A></li>
            ))}
          </Col>
          <Col title={t("Investor")}>
            {INVESTOR_LINKS.map((n) => (
              <li key={n.href}><A href={n.href} external={n.external} testid={`footer-investor-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>{n.label}</A></li>
            ))}
          </Col>
        </div>

        <div className="mt-20 border-t border-white/15 pt-8">
          <h3 className="t-label text-white/70">{t("Selskapene i EQVA")}</h3>
          <ul className="mt-7 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-8 gap-y-8" data-testid="footer-companies">
            {companies.map((c) => (
              <li key={c.slug}>
                <Link to={`/selskaper/${c.slug}`} data-testid={`footer-company-${c.slug}`} aria-label={c.name} className="group block opacity-70 transition-opacity duration-200 hover:opacity-100 focus-visible:opacity-100">
                  <CompanyLogo company={c} variant="white" height={24} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/15">
        <div className="wrap py-6 flex flex-col md:flex-row justify-between gap-3 t-small text-white/60">
          <p>© {new Date().getFullYear()} EQVA ASA · {t("Notert på Euronext Oslo Børs")} · ISIN NO0010708605</p>
          <p className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/personvern" data-testid="footer-link-personvern" className="hover:text-white transition-colors">{t("Personvern")}</Link>
            <a href={langHref(isEn ? "no" : "en")} data-testid="footer-link-english" className="hover:text-white transition-colors" lang={isEn ? "nb" : "en"} hrefLang={isEn ? "nb" : "en"}>{isEn ? "Norsk" : "English"}</a>
            <span>{t("Kursdata kan være forsinket.")} <Link to="/investor" className="hover:text-white transition-colors underline-offset-2 hover:underline">{t("Se investorinformasjon")}</Link>.</span>
          </p>
        </div>
      </div>
    </footer>
  );
};
